"use client";

import { useEffect, useRef } from "react";

/**
 * Toggles a `data-scrolled` attribute on the returned element ref when the
 * window is scrolled past `threshold`, writing directly to the DOM instead of
 * React state. This keeps the (potentially large) navbar subtree from
 * re-rendering on every scroll threshold crossing, which otherwise drops a
 * frame and produces a visible jag mid-scroll.
 */
export const useScrolledAttribute = <T extends HTMLElement>(
  threshold = 100,
) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let ticking = false;

    const update = () => {
      ticking = false;
      const scrolled = window.scrollY > threshold;
      const next = scrolled ? "true" : "false";
      if (element.dataset.scrolled !== next) {
        element.dataset.scrolled = next;
      }
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [threshold]);

  return ref;
};
